import { find, isMatch, filter, sortBy } from 'lodash-es';

export const RESERVED_OBJECT_METHODS = ['runCallbacks', 'set', 'update', 'subscribe', 'subscribeFns', 'isInitialized'];

function ObjectStore(instance) {
  if (typeof instance !== 'object') {
    instance = { value: instance };
  }

  instance.subscribeFns = [];
  instance.isNew = instance.isNew === undefined ? true : instance.isNew;
  instance.isInitialized = true;

  instance.runCallbacks = function (previousObject) {
    instance.subscribeFns.forEach((fn) => fn && fn(instance, previousObject));
  };

  instance.set = function (value) {
    let previousObject = { ...instance };

    Object.keys(instance).forEach((key) => {
      if (!RESERVED_OBJECT_METHODS.includes(key)) {
        delete instance[key];
      }
    });

    Object.keys(value).forEach((key) => {
      instance[key] = value[key];
    });

    instance.isNew = false;
    instance.runCallbacks(previousObject);
  };

  instance.update = function (callback) {
    var newVal = callback(JSON.parse(JSON.stringify(instance)));
    instance.set(newVal);
  };

  instance.subscribe = function (callback) {
    instance.subscribeFns.push(callback);
    callback(instance);

    return function () {
      instance.subscribeFns = instance.subscribeFns.filter((fn) => fn !== callback);
    };
  };

  return instance;
}

function CollectionStore(initialValue) {
  let objectsById = {};
  let instance = [];
  (initialValue || []).forEach(function (x) {
    var os = ObjectStore(x);
    instance.push(os); // You still want to push the object to the instance (the array)
    objectsById[os.id] = os; // But also map it in the objectsById for quick access by ID
  });

  instance.subscribeFns = [];
  instance.isNew = instance.isNew === undefined ? true : instance.isNew;
  instance.isInitialized = true;
  instance.runCallbacks = function () {
    instance.subscribeFns.forEach((fn) => fn && fn(instance));
  };

  instance.set = function (values) {
    instance.clear(false);

    if (values) {
      values.forEach(function (x) {
        var os = ObjectStore(x);
        instance.push(os);
        objectsById[os.id] = os;
      });
    }

    instance.isNew = false;
    instance.runCallbacks();
  };

  instance.addCollection = function (values) {
    if (values) {
      values.forEach(function (x) {
        var os = ObjectStore(x);
        instance.push(os);
        objectsById[os.id] = os;
      });
    }

    instance.isNew = false;
    instance.runCallbacks();
  };

  instance.update = function (callback) {
    var newVal = callback(JSON.parse(JSON.stringify(instance)));
    instance.set(newVal);
  };

  instance.find = function (idOrCallback) {
    if (typeof idOrCallback === 'function') {
      return Array.prototype.find.call(this, idOrCallback);
    }

    return objectsById[idOrCallback]; // optimized way of finding a single object by id: O(1)
  };

  instance.findByIds = function (ids) {
    let hashIds = {};

    ids.forEach((x) => {
      hashIds[x] = true;
    });

    return instance.filter((x) => hashIds[x.id]);
  };

  instance.one = function (params) {
    return find(instance, (x) => {
      return isMatch(x, params);
    });
  };

  instance.where = function (params) {
    return filter(instance, (x) => {
      return isMatch(x, params);
    });
  };

  instance.withSortOrder = function (orderIds) {
    return instance.sortBy((x) => {
      var idx = orderIds.indexOf(x.id);
      if (idx == -1) {
        idx = 9999;
      }
      return idx;
    });
  };

  instance.withSortOrderUnsortedFirst = function (orderIds) {
    return instance.sortBy((x) => {
      return orderIds.indexOf(x.id);
    });
  };

  instance.sortBy = function (fn) {
    return sortBy(instance, fn);
  };

  instance.subscribe = function (callback) {
    instance.subscribeFns.push(callback);
    callback(instance);

    return function () {
      instance.subscribeFns = instance.subscribeFns.filter((fn) => fn !== callback);
    };
  };

  instance.count = function () {
    return instance.length;
  };

  instance.addFront = function (data) {
    let os = ObjectStore(data);
    instance.unshift(os);
    objectsById[os.id] = os;
    instance.runCallbacks();
  };

  instance.add = function (data) {
    let os = ObjectStore(data);
    instance.push(os);
    objectsById[os.id] = os;
    instance.runCallbacks();
  };

  instance.clear = function (callbacks = true) {
    instance.length = 0;
    objectsById = {};

    if (callbacks) {
      instance.runCallbacks();
    }
  };

  instance.remove = function (data) {
    var obj = instance.one(data);

    if (obj) {
      instance.splice(instance.indexOf(obj), 1);
      delete objectsById[data.id];
      instance.runCallbacks();
    }
  };

  instance.distinctReduce = function (query, reduceFn) {
    return [...new Set(instance.where(query).map((x) => reduceFn(x)))];
  };

  instance.distinctReduceCount = function (query, reduceFn) {
    let ids = instance.where(query).map((x) => reduceFn(x));
    return [...new Set(ids)].length;
  };

  return instance;
}

export { ObjectStore, CollectionStore };
